import React, { useState, useEffect } from 'react';
import { Plus, Minus, ArrowRightLeft, Package, AlertTriangle, CreditCard as Edit, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useInventory } from '../../context/InventoryContext';
import { supabase } from '../../lib/supabase';
import { Product, Location, ProductLocation, LocationTransfer } from '../../types';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Badge from '../ui/Badge';
import LocationTransferForm from './LocationTransferForm';
import TransferToWasteForm from './TransferToWasteForm';

interface ProductLocationManagerProps {
  product: Product;
  onStockUpdate?: () => void;
}

const ProductLocationManager: React.FC<ProductLocationManagerProps> = ({
  product,
  onStockUpdate
}) => {
  const { user } = useAuth();
  const { hasStockMismatch } = useInventory();
  const [locations, setLocations] = useState<Location[]>([]);
  const [productLocations, setProductLocations] = useState<ProductLocation[]>([]);
  const [transfers, setTransfers] = useState<LocationTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [showStockForm, setShowStockForm] = useState(false);
  const [showLocationError, setShowLocationError] = useState(false);
  const [showEditLocationForm, setShowEditLocationForm] = useState(false);
  const [showWasteTransferForm, setShowWasteTransferForm] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<ProductLocation | null>(null);
  
  const [transferForm, setTransferForm] = useState({
    from_location_id: '',
    to_location_id: '',
    quantity: 1,
    reason: '',
    notes: ''
  });

  const [stockForm, setStockForm] = useState({
    location_id: '',
    quantity: 0,
    type: 'in' as 'in' | 'out' | 'adjustment',
    notes: ''
  });

  const [editLocationForm, setEditLocationForm] = useState({
    quantity: 0,
    min_stock_level: 0,
    max_stock_level: 0
  });

  useEffect(() => {
    loadData();
  }, [product.id]);

  const loadData = async () => {
    console.log('Loading data for product:', product.id);
    try {
      // Load locations
      console.log('Fetching locations...');
      const { data: locationsData, error: locationsError } = await supabase
        .from('locations')
        .select('*')
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('name');

      if (locationsError) {
        console.error('Locations error:', locationsError);
        throw locationsError;
      }
      console.log('Locations loaded:', locationsData?.length || 0);
      setLocations(locationsData || []);

      // Load product locations
      console.log('Fetching product locations...');
      const { data: productLocationsData, error: productLocationsError } = await supabase
        .from('product_locations')
        .select(`
          *,
          location:locations(*)
        `)
        .eq('product_id', product.id);

      if (productLocationsError) {
        console.error('Product locations error:', productLocationsError);
        throw productLocationsError;
      }
      console.log('Product locations loaded:', productLocationsData?.length || 0);
      setProductLocations(productLocationsData || []);

      // Load recent transfers
      console.log('Fetching transfers...');
      const { data: transfersData, error: transfersError } = await supabase
        .from('location_transfers')
        .select(`
          *,
          from_location:locations!location_transfers_from_location_id_fkey(*),
          to_location:locations!location_transfers_to_location_id_fkey(*)
        `)
        .eq('product_id', product.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (transfersError) {
        console.error('Transfers error:', transfersError);
        throw transfersError;
      }
      console.log('Transfers loaded:', transfersData?.length || 0);
      setTransfers(transfersData || []);
      
      console.log('All data loaded successfully');
    } catch (error) {
      console.error('Error loading data:', error);
      
      // More detailed error information
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        console.error('Network error - possible causes:');
        console.error('1. Supabase URL is incorrect');
        console.error('2. Network connectivity issues');
        console.error('3. CORS issues');
        console.error('4. Supabase service is down');
        
        // Show user-friendly error
        alert('Unable to connect to the database. Please check your internet connection and try again.');
      } else {
        console.error('Detailed error:', {
          message: error?.message,
          code: error?.code,
          details: error?.details,
          hint: error?.hint
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStockUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const quantityChange = stockForm.type === 'out' ? -stockForm.quantity : stockForm.quantity;
      
      const { error } = await supabase.rpc('update_product_location_stock', {
        p_product_id: product.id,
        p_location_id: stockForm.location_id,
        p_quantity_change: quantityChange,
        p_transaction_type: stockForm.type
      });

      if (error) throw error;

      // Create transaction record
      await supabase.from('transactions').insert({
        user_id: user?.id,
        product_id: product.id,
        type: stockForm.type,
        quantity: stockForm.quantity,
        notes: stockForm.notes,
        metadata: {
          location_id: stockForm.location_id,
          location_name: locations.find(l => l.id === stockForm.location_id)?.name
        }
      });

      setShowStockForm(false);
      setStockForm({
        location_id: '',
        quantity: 0,
        type: 'in',
        notes: ''
      });
      loadData();
      onStockUpdate?.();
    } catch (error: any) {
      console.error('Error updating stock:', error);
      alert(error.message || 'Failed to update stock');
    }
  };

  const updateProductLocation = async () => {
    try {
      setLoading(true);

      // First, try to find a location matching the product's location text field
      let targetLocation = product.location
        ? locations.find(loc => loc.name.toLowerCase() === product.location.toLowerCase())
        : null;

      // If no match found, fall back to the default location
      if (!targetLocation) {
        targetLocation = locations.find(loc => loc.is_default);
      }

      if (!targetLocation) {
        alert('No suitable location found. Please set a default location or ensure the product location name matches an existing location.');
        return;
      }

      // Check if product location already exists for target location
      const existingLocation = productLocations.find(pl => pl.location_id === targetLocation.id);

      if (existingLocation) {
        // Update existing location with product stock
        const { error } = await supabase
          .from('product_locations')
          .update({
            quantity: product.stock_quantity,
            min_stock_level: product.min_stock_level
          })
          .eq('id', existingLocation.id);

        if (error) throw error;
      } else {
        // Create new product location record
        const { error } = await supabase
          .from('product_locations')
          .insert({
            product_id: product.id,
            location_id: targetLocation.id,
            quantity: product.stock_quantity,
            min_stock_level: product.min_stock_level
          });

        if (error) throw error;
      }

      // Update the product's location field to match
      const { error: updateError } = await supabase
        .from('supplies')
        .update({
          location: targetLocation.name
        })
        .eq('id', product.id);

      if (updateError) throw updateError;

      setShowLocationError(false);
      await loadData();
      onStockUpdate?.();
    } catch (error) {
      console.error('Error updating product location:', error);
      alert('Failed to update product location. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedLocation) return;
    
    try {
      const { error } = await supabase
        .from('product_locations')
        .update({
          quantity: editLocationForm.quantity,
          min_stock_level: editLocationForm.min_stock_level,
          max_stock_level: editLocationForm.max_stock_level || null
        })
        .eq('id', selectedLocation.id);

      if (error) throw error;
      
      setShowEditLocationForm(false);
      setSelectedLocation(null);
      loadData();
      onStockUpdate?.();
    } catch (error: any) {
      console.error('Error updating location:', error);
      alert(error.message || 'Failed to update location');
    }
  };

  const getTotalStock = () => {
    const locationStock = productLocations.reduce((total, pl) => total + pl.quantity, 0);

    // If there are location-based stock records, use those
    if (locationStock > 0) {
      return locationStock;
    }

    // Otherwise, fall back to the product's stock_quantity field
    return product.stock_quantity || 0;
  };

  const getLowStockLocations = () => {
    return productLocations.filter(pl => pl.quantity <= pl.min_stock_level);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {/* Summary */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <CardTitle>Location Inventory</CardTitle>
              {hasStockMismatch(product) && (
                <Badge variant="danger" className="ml-2">Stock Mismatch</Badge>
              )}
            </div>
            {hasStockMismatch(product) && (
                <Button
                  variant="warning"
                  onClick={updateProductLocation}
                  className="mr-2"
                  disabled={loading}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Fix Stock Mismatch
                  {loading && (
                    <span className="ml-2 animate-spin">⟳</span>
                  )}
                </Button>
            )}
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowStockForm(true)}
              >
                <Package className="h-4 w-4 mr-2" />
                Update Stock
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowTransferForm(true)}
              >
                <ArrowRightLeft className="h-4 w-4 mr-2" />
                Transfer
              </Button>
              <Button
                variant="danger"
                onClick={() => setShowWasteTransferForm(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                To Waste
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {hasStockMismatch(product) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Stock Mismatch Detected
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    Product shows {product.stock_quantity} {product.unit_of_measure} in stock, but location totals show {getTotalStock()} {product.unit_of_measure}.
                    Click "Fix Stock Mismatch" to sync the quantities.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-900">{getTotalStock()}</div>
              <div className="text-sm text-blue-600">Total Stock ({product.unit_of_measure})</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-900">{productLocations.length}</div>
              <div className="text-sm text-green-600">Locations</div>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <div className="text-2xl font-bold text-amber-900">{getLowStockLocations().length}</div>
              <div className="text-sm text-amber-600">Low Stock</div>
            </div>
          </div>

          {/* Location Stock Details */}
          <div className="space-y-3">
            {productLocations.map((pl) => (
              <div
                key={pl.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-gray-50 rounded-full mr-3">
                    <Package className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{pl.location?.name}</h4>
                    <p className="text-sm text-gray-500">
                      Min: {pl.min_stock_level} {product.unit_of_measure}
                      {pl.max_stock_level && ` | Max: ${pl.max_stock_level} ${product.unit_of_measure}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {pl.quantity <= pl.min_stock_level && (
                    <Badge variant="warning">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Low Stock
                    </Badge>
                  )}
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{pl.quantity}</div>
                    <div className="text-sm text-gray-500">{product.unit_of_measure}</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedLocation(pl);
                      setEditLocationForm({
                        quantity: pl.quantity,
                        min_stock_level: pl.min_stock_level,
                        max_stock_level: pl.max_stock_level || 0
                      });
                      setShowEditLocationForm(true);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {productLocations.length === 0 && (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No stock locations</h3>
              <p className="text-gray-500 mb-4">Add stock to locations to get started</p>
              <Button onClick={() => setShowStockForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Stock
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Transfers */}
      {transfers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Transfers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {transfers.map((transfer) => (
                <div
                  key={transfer.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-50 rounded-full mr-3">
                      <ArrowRightLeft className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {transfer.from_location?.name} → {transfer.to_location?.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(transfer.created_at).toLocaleDateString()}
                        {transfer.reason && ` • ${transfer.reason}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={
                      transfer.status === 'completed' ? 'success' :
                      transfer.status === 'pending' ? 'warning' : 'danger'
                    }>
                      {transfer.status}
                    </Badge>
                    <div className="text-sm font-medium text-gray-900">
                      {transfer.quantity} {product.unit_of_measure}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transfer Form Modal */}
      {showTransferForm && product && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4 py-8">
            <div className="w-full max-w-md">
              <LocationTransferForm
                productId={product.id}
                onTransferComplete={() => {
                  setShowTransferForm(false);
                  loadData();
                  onStockUpdate?.();
                }}
                onCancel={() => setShowTransferForm(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Stock Update Form Modal */}
      {showStockForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4 py-8">
            <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Update Stock</CardTitle>
            </CardHeader>
            <form onSubmit={handleStockUpdate}>
              <CardContent className="space-y-4">
                <Select
                  label="Location"
                  options={[
                    { value: '', label: 'Select location' },
                    ...locations.map(location => ({
                      value: location.id,
                      label: location.name
                    }))
                  ]}
                  value={stockForm.location_id}
                  onChange={(value) => setStockForm({ ...stockForm, location_id: value })}
                  required
                  fullWidth
                />

                <Select
                  label="Transaction Type"
                  options={[
                    { value: 'in', label: 'Stock In' },
                    { value: 'out', label: 'Stock Out' },
                    { value: 'adjustment', label: 'Adjustment' }
                  ]}
                  value={stockForm.type}
                  onChange={(value) => setStockForm({ ...stockForm, type: value as 'in' | 'out' | 'adjustment' })}
                  required
                  fullWidth
                />

                <Input
                  label={`Quantity (${product.unit_of_measure})`}
                  type="number"
                  min="1"
                  value={stockForm.quantity}
                  onChange={(e) => setStockForm({ ...stockForm, quantity: parseInt(e.target.value) || 0 })}
                  helperText={`Enter the quantity in ${product.unit_of_measure}`}
                  required
                  fullWidth
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={stockForm.notes}
                    onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                  />
                </div>
              </CardContent>
              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowStockForm(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Update Stock
                </Button>
              </div>
            </form>
          </Card>
          </div>
        </div>
      )}

      {/* Edit Location Form Modal */}
      {showEditLocationForm && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4 py-8">
            <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Edit Location Stock</CardTitle>
            </CardHeader>
            <form onSubmit={handleEditLocation}>
              <CardContent className="space-y-4">
                <div className="bg-gray-50 p-3 rounded-lg mb-4">
                  <p className="text-sm font-medium text-gray-900">
                    Location: {selectedLocation.location?.name}
                  </p>
                </div>

                <Input
                  label={`Current Quantity (${product.unit_of_measure})`}
                  type="number"
                  min="0"
                  value={editLocationForm.quantity}
                  onChange={(e) => setEditLocationForm({
                    ...editLocationForm,
                    quantity: parseInt(e.target.value) || 0
                  })}
                  required
                  fullWidth
                />

                <Input
                  label={`Minimum Stock Level (${product.unit_of_measure})`}
                  type="number"
                  min="0"
                  value={editLocationForm.min_stock_level}
                  onChange={(e) => setEditLocationForm({
                    ...editLocationForm,
                    min_stock_level: parseInt(e.target.value) || 0
                  })}
                  fullWidth
                />

                <Input
                  label={`Maximum Stock Level (${product.unit_of_measure})`}
                  type="number"
                  min="0"
                  value={editLocationForm.max_stock_level}
                  onChange={(e) => setEditLocationForm({
                    ...editLocationForm,
                    max_stock_level: parseInt(e.target.value) || 0
                  })}
                  helperText="Optional: Set to 0 for no maximum"
                  fullWidth
                />
              </CardContent>
              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowEditLocationForm(false);
                    setSelectedLocation(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Save Changes
                </Button>
              </div>
            </form>
          </Card>
          </div>
        </div>
      )}

      {/* Transfer to Waste Form Modal */}
      {showWasteTransferForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4 py-8">
            <TransferToWasteForm
              sourceType="supplies"
              sourceItem={product}
              productLocations={productLocations}
              onTransferComplete={() => {
                setShowWasteTransferForm(false);
                loadData();
                onStockUpdate?.();
              }}
              onCancel={() => setShowWasteTransferForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductLocationManager;